import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import QRCode from "react-qr-code";
import {
  Wallet, Smartphone, QrCode, ArrowRight, CalendarPlus, ScanLine, Ticket,
  Check, CalendarDays, MapPin, Users, BarChart3, Quote, FileDown, BookOpen } from "lucide-react";
import { PublicLayout } from "../components/PublicLayout";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import { api, formatDateTime, priceLabel, organizerGuideUrl } from "../lib/api";
import { useLang } from "../context/LanguageContext";

const FLOW = [
  { icon: CalendarPlus, label: "Opprett" },
  { icon: Ticket, label: "Selg" },
  { icon: Smartphone, label: "Vipps" },
  { icon: ScanLine, label: "QR-innsjekk" },
];

const PRICE_POINTS = [
  "Ingen betaling før arrangementet er over",
  "Ingen gebyrpåslag for publikum – prisen du setter er prisen de betaler",
  "Pengene går rett til din konto via ditt eget Vipps-oppsett",
  "Ingen faste kostnader, ingen binding",
];

// Sitater fra arrangører. Bytt gjerne ut med ekte sitater etter hvert som de kommer inn.
const QUOTES = [
  {
    text: "Vi var i gang på under ti minutter, og innsjekkingen med QR gikk helt knirkefritt ved døra.",
    source: "Arrangør av kulturkveld, Vestland",
  },
  {
    text: "Publikum slapp å lage konto, og pengene gikk rett inn på vår konto via Vipps. Akkurat så enkelt som lovet.",
    source: "Frivillig lag og forening",
  },
  {
    text: "Ukesrapporten på e-post gir oss full kontroll på salget uten at vi trenger å logge inn hver dag.",
    source: "Konsertarrangør",
  },
];

const BENEFITS = [
  {
    icon: Wallet,
    title: "Ditt eget Vipps-oppsett",
    text: "Publikum betaler med Vipps, og pengene går rett til din konto – ikke via oss.",
  },
  {
    icon: QrCode,
    title: "QR-innsjekk med mobilen",
    text: "Skann billetter rett fra telefonen ved døra. Gyldig, brukt eller ugyldig – svar på sekundet.",
  },
  {
    icon: Users,
    title: "Ingen konto for publikum",
    text: "E-post er alt som trengs. Billettene er klare på sekunder – færre hindringer, flere solgte billetter.",
  },
  {
    icon: BarChart3,
    title: "Full oversikt",
    text: "Salg, visninger og innsjekkinger i dashbordet – og ukesrapport rett i innboksen.",
  },
];

export default function ForOrganizersPage() {
  const { t, lang } = useLang();
  const [events, setEvents] = useState([]);

  useEffect(() => {
    api
      .get("/events")
      .then((res) => setEvents((res.data || []).slice(0, 3)))
      .catch(() => {});
  }, []);

  return (
    <PublicLayout>
      {/* Hero */}
      <section className="hero-decor border-b border-border">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="lg:col-span-7 space-y-6"
          >
            <Badge variant="secondary" className="text-xs font-medium" data-testid="landing-hero-badge">
              {t("For arrangører")} · rightin.no
            </Badge>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05]" data-testid="landing-hero-title">
              {t("Billettsalg")}
              <br />
              {t("gjort enkelt.")}
            </h1>
            <p className="text-base sm:text-lg text-muted-foreground max-w-xl leading-relaxed">
              {t("Selg billetter med ditt eget Vipps-oppsett – pengene går rett til deg. QR-innsjekk ved døra, og én enkel pris.")}
            </p>
            <div className="flex flex-wrap gap-3 pt-1">
              <Button asChild size="lg" className="h-12 px-6" data-testid="landing-hero-cta-register">
                <Link to="/registrer">
                  {t("Opprett arrangement")}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="secondary" className="h-12 px-6" data-testid="landing-hero-cta-price">
                <a href="#pris">{t("Se prisen")}</a>
              </Button>
            </div>
            <div className="flex flex-wrap gap-6 pt-2 text-sm text-muted-foreground">
              <span className="flex items-center gap-2"><Smartphone className="w-4 h-4" /> {t("Eget Vipps-oppsett")}</span>
              <span className="flex items-center gap-2"><QrCode className="w-4 h-4" /> {t("QR-innsjekk")}</span>
              <span className="flex items-center gap-2"><Wallet className="w-4 h-4" /> {t("Enkel pris: 2,9 %")}</span>
            </div>
          </motion.div>

          {/* Phone mockup with QR ticket */}
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.1 }}
            className="lg:col-span-5 flex justify-center lg:justify-end"
            aria-hidden="true"
          >
            <div className="relative w-[270px] sm:w-[290px]" data-testid="landing-phone-mockup">
              <div className="rounded-[2.6rem] border-[10px] border-ink bg-ink shadow-xl">
                <div className="rounded-[2rem] bg-background overflow-hidden">
                  <div className="bg-ink text-ink-foreground px-5 pt-4 pb-3">
                    <div className="mx-auto mb-3 h-1.5 w-16 rounded-full bg-ink-foreground/25" />
                    <p className="text-[11px] uppercase tracking-wide opacity-70">{t("Billett")} · Right in</p>
                    <p className="font-heading font-semibold leading-snug">{t("Sommerkonsert i parken")}</p>
                    <p className="text-xs opacity-75 mt-0.5">{t("Lørdag 20. juni kl. 19:00")}</p>
                  </div>
                  <div className="p-5 flex flex-col items-center gap-3">
                    <div className="bg-white p-3 rounded-xl border border-border">
                      <QRCode value="RIGHTIN-DEMO-BILLETT" size={140} />
                    </div>
                    <p className="font-mono font-semibold tracking-widest text-sm">A7K2-M9QX</p>
                    <Badge className="bg-[hsl(var(--success))] text-[hsl(var(--success-foreground))] hover:bg-[hsl(var(--success))]">
                      {t("Gyldig")}
                    </Badge>
                    <div className="w-full rounded-lg bg-secondary px-3 py-2 flex items-center justify-between text-xs">
                      <span className="text-muted-foreground">{t("Betalt med Vipps")}</span>
                      <span className="font-semibold tabular-nums">250 kr</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Flow strip: Opprett -> Selg -> Vipps -> QR -> Right in */}
        <div className="border-t border-border bg-card">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-4 sm:gap-x-5" data-testid="landing-flow-strip">
              {FLOW.map((step, i) => (
                <span key={step.label} className="flex items-center gap-3 sm:gap-5">
                  <span className="flex items-center gap-2.5">
                    <span className="w-9 h-9 rounded-xl bg-brand-soft text-brand flex items-center justify-center">
                      <step.icon className="w-4.5 h-4.5" />
                    </span>
                    <span className="text-sm font-medium">{t(step.label)}</span>
                  </span>
                  <ArrowRight className="w-4 h-4 text-muted-foreground" />
                </span>
              ))}
              <span className="font-heading font-semibold text-sm">
                Right <span className="text-brand">in</span>
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pris" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
        <div className="rounded-2xl bg-ink text-ink-foreground overflow-hidden" data-testid="landing-pricing-card">
          <div className="grid grid-cols-1 lg:grid-cols-2">
            <div className="px-6 sm:px-12 py-12 sm:py-14 space-y-3">
              <p className="text-sm uppercase tracking-wide text-primary-foreground/60">{t("Én enkel pris")}</p>
              <p className="font-heading font-semibold leading-none">
                <span className="text-6xl sm:text-7xl tabular-nums" data-testid="landing-price-percent">2,9&nbsp;%</span>
                <span className="text-lg text-primary-foreground/75 ml-3">{t("per solgte billett")}</span>
              </p>
              <p className="text-primary-foreground/75" data-testid="landing-price-minimum">
                {t("Minimum 99 kr per gjennomførte arrangement. Ingen betaling før arrangementet er over.")}
              </p>
              <p className="text-sm">
                <Link to="/pris" className="underline underline-offset-4 text-primary-foreground/75 hover:text-primary-foreground transition-colors duration-150" data-testid="landing-pricing-faq-link">
                  {t("Vanlige spørsmål om prisen")}
                </Link>
              </p>
              <div className="pt-3">
                <Button asChild size="lg" variant="secondary" className="h-12 px-6" data-testid="landing-pricing-cta">
                  <Link to="/registrer">
                    {t("Prøv Right in")}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Link>
                </Button>
              </div>
            </div>
            <div className="px-6 sm:px-12 py-12 sm:py-14 lg:border-l border-t lg:border-t-0 border-primary-foreground/15">
              <ul className="space-y-4" data-testid="landing-pricing-points">
                {PRICE_POINTS.map((point) => (
                  <li key={point} className="flex items-start gap-3 text-sm sm:text-base">
                    <span className="mt-0.5 w-5 h-5 rounded-full bg-[hsl(var(--success))] text-[hsl(var(--success-foreground))] flex items-center justify-center shrink-0">
                      <Check className="w-3 h-3" />
                    </span>
                    <span className="text-primary-foreground/90">{t(point)}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Real events */}
      {events.length > 0 && (
        <section className="border-y border-border bg-card">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
            <div className="flex items-end justify-between gap-4 mb-8 flex-wrap">
              <div>
                <h2 className="text-2xl sm:text-3xl font-heading font-semibold tracking-tight">
                  {t("Live på Right in akkurat nå")}
                </h2>
                <p className="text-muted-foreground mt-2">{t("Ekte arrangementer som selger billetter i dag.")}</p>
              </div>
              <Button asChild variant="ghost" size="sm" data-testid="landing-events-see-all">
                <Link to="/">
                  {t("Se alle arrangementer")}
                  <ArrowRight className="w-4 h-4 ml-1.5" />
                </Link>
              </Button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="landing-live-events">
              {events.map((event) => (
                <Link key={event.id} to={`/arrangement/${event.id}`} className="group" data-testid={`landing-event-card-${event.id}`}>
                  <Card className="border-border overflow-hidden h-full transition-shadow duration-200 group-hover:shadow-md">
                    <div className="aspect-video bg-secondary overflow-hidden">
                      {event.image_url ? (
                        <img
                          src={event.image_url}
                          alt={event.title}
                          loading="lazy"
                          className="w-full h-full object-cover transition-transform duration-200 group-hover:scale-[1.02]"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                          <CalendarDays className="w-8 h-8" />
                        </div>
                      )}
                    </div>
                    <CardContent className="p-4 space-y-2">
                      <div className="flex items-start justify-between gap-3">
                        <h3 className="font-heading font-semibold leading-snug">{event.title}</h3>
                        <Badge variant="secondary" className="shrink-0 tabular-nums">{priceLabel(event)}</Badge>
                      </div>
                      <p className="text-sm text-muted-foreground flex items-center gap-1.5">
                        <CalendarDays className="w-3.5 h-3.5" />
                        {formatDateTime(event.date)}
                      </p>
                      <p className="text-sm text-muted-foreground flex items-center gap-1.5">
                        <MapPin className="w-3.5 h-3.5" />
                        {[event.venue, event.kommune || event.city].filter(Boolean).join(", ")}
                      </p>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Testimonials */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
        <h2 className="text-2xl sm:text-3xl font-heading font-semibold tracking-tight mb-8">
          {t("Arrangører om Right in")}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5" data-testid="landing-testimonials">
          {QUOTES.map((quote, i) => (
            <Card key={i} className="border-border" data-testid={`landing-quote-${i}`}>
              <CardContent className="p-6 space-y-4">
                <Quote className="w-6 h-6 text-brand" aria-hidden="true" />
                <p className="text-sm sm:text-base leading-relaxed">«{t(quote.text)}»</p>
                <p className="text-xs text-muted-foreground">— {t(quote.source)}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Benefits - short and visual */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-14 sm:pb-20">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="landing-benefits-grid">
          {BENEFITS.map((b) => (
            <Card key={b.title} className="border-border hover:shadow-md transition-shadow duration-200">
              <CardContent className="p-5 space-y-3">
                <span className="w-10 h-10 rounded-xl bg-brand-soft text-brand flex items-center justify-center">
                  <b.icon className="w-5 h-5" />
                </span>
                <h3 className="font-heading font-semibold">{t(b.title)}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{t(b.text)}</p>
              </CardContent>
            </Card>
          ))}
        </div>
        {/* Ressurs: kom-i-gang-veiledning (PDF) */}
        <div className="mt-6 rounded-2xl border border-brand/40 bg-brand-soft/30 px-5 sm:px-8 py-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-testid="landing-organizer-guide">
          <div className="flex items-start gap-4">
            <span className="w-10 h-10 shrink-0 rounded-xl bg-brand text-primary-foreground flex items-center justify-center">
              <BookOpen className="w-5 h-5" />
            </span>
            <div className="space-y-1">
              <h3 className="font-heading font-semibold">{t("Så enkelt er det å komme i gang med Right in")}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {t("Veiledning for arrangører på fire sider: konto, kontaktinfo, arrangement, deling og plakat, innsjekk, rapporter og pris – pluss «Koble til Vipps – steg for steg». Tips: trykk F1 hvor som helst på Right in for å åpne den.")}
              </p>
            </div>
          </div>
          <Button asChild className="shrink-0" data-testid="landing-organizer-guide-button">
            <a href={organizerGuideUrl(lang)} target="_blank" rel="noopener noreferrer">
              <FileDown className="w-4 h-4 mr-1.5" />
              {t("Last ned veiledningen (PDF)")}
            </a>
          </Button>
        </div>
        {/* Ressurs: instruks til de som står i døren */}
        <div className="mt-6 rounded-2xl border border-border bg-card px-5 sm:px-8 py-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-testid="landing-checkin-guide">
          <div className="flex items-start gap-4">
            <span className="w-10 h-10 shrink-0 rounded-xl bg-brand-soft text-brand flex items-center justify-center">
              <FileDown className="w-5 h-5" />
            </span>
            <div className="space-y-1">
              <h3 className="font-heading font-semibold">{t("Instruks til de som står i døren")}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {t("Én side som forklarer innsjekk med mobilen – skann, farger og manuell kode. Skriv ut eller send til frivillige. Innlogget finner du en utgave ferdig utfylt med ditt arrangement under «Sjekk inn».")}
              </p>
            </div>
          </div>
          <Button asChild variant="secondary" className="shrink-0" data-testid="landing-checkin-guide-button">
            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/resources/checkin-instructions.pdf?lang=${lang}`} target="_blank" rel="noopener noreferrer">
              <FileDown className="w-4 h-4 mr-1.5" />
              {t("Last ned PDF")}
            </a>
          </Button>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="rounded-2xl border border-border bg-card px-6 sm:px-12 py-12 sm:py-14 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
          <div className="space-y-2 max-w-xl">
            <h2 className="text-2xl sm:text-3xl font-heading font-semibold tracking-tight">
              {t("Klar til å selge billetter?")}
            </h2>
            <p className="text-muted-foreground">
              {t("Opprett arrangementet i dag – ingen betaling før arrangementet er over.")}
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Button asChild size="lg" className="h-12 px-6" data-testid="landing-bottom-cta-register">
              <Link to="/registrer">
                {t("Opprett arrangement")}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </Button>
            <Button asChild size="lg" variant="secondary" className="h-12 px-6" data-testid="landing-bottom-cta-login">
              <Link to="/logg-inn">{t("Logg inn")}</Link>
            </Button>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
